
import { Injectable } from "@angular/core";
import { TemporaryArea } from "@models/map/market-filter.model";
import { CapitalizePipe } from "ev-core";
import * as L from "leaflet";
import { IGroupsWithMetaData, ILatLng } from "polydraw";
import { EvAreaType } from "src/app/infopage/models/area-type.enum";

@Injectable({
    providedIn: "root",
})
export class AreaBuilderUtils {

    mapToMetaData(latlng: L.LatLng[][][], area: TemporaryArea): IGroupsWithMetaData {
        return {
            layer: latlng,
            metaData: {
                areaTypeId: area.areaType,
                areaId: area.areaId,
                customName: area.name,
                customId: area.number,
                mergeable: true,
                draggable: false,
                general: {
                    parentAreaType: area.parentAreaType,
                    parentAreaId: area.parentAreaId
                },
                infoMarkerContent: [[
                    this.getAreaTypeNameSingular(area.areaType) + ':',
                    this.getDisplayName(area)
                ]]
            },
            styles: { fillColor: "#3d91c6", color: "#3d91c6" }
        }
    }

    getAreasFromLayers(layers: L.Layer[][], withGeoBorder = false): TemporaryArea[] {
        const areas: any[] = [];
        layers.forEach((layer) => {
            layer.forEach((l: any) => {
                const isDrawn = !l?.options?.metaData?.areaId; 
                if (!isDrawn) {
                    if (areas.some(a => a.areaId === l.options.metaData.areaId)) {
                        const area = areas.find(a => a.areaId === l.options.metaData.areaId);
                        area.geographicBorder = area.geographicBorder.concat(withGeoBorder ? l.getLatLngs() : []);
                    } else {
                        const metaData = l.options.metaData;
                        areas.push({
                            areaType: metaData.areaTypeId,
                            areaId: metaData.areaId,
                            name: metaData.customName,
                            number: metaData.customId,
                            parentAreaId: metaData?.general?.parentAreaId,
                            parentAreaType: metaData?.general?.parentAreaType,
                            geographicBorder: withGeoBorder ? l.getLatLngs() : []
                        } as TemporaryArea)
                    }
                }
            });
        });
        return areas;
    }

    getDrawnPolygonsFromLayers(layers: L.Layer[][]): ILatLng[][][] {
        let drawnPolygons: any[] = [];
        layers.forEach((layer) => {
            layer.forEach((l: any) => {
                const isDrawn = !l?.options?.metaData?.areaId; 
                if (isDrawn) {
                    const latlng = l.getLatLngs();
                    drawnPolygons = drawnPolygons.concat(latlng);
                }
            });
        });
        return drawnPolygons;
    }

    getFeatureGroups(polygons: ILatLng[][][], areas: TemporaryArea[]): IGroupsWithMetaData[] {
        const featureGroups: any[] = [];
        if (areas) {
            areas.forEach(area => {
                const latlng = this.convertToLeafletLatLngArray3D(area.geographicBorder);
                featureGroups.push(this.mapToMetaData(latlng, area));
            });
        }

        if (polygons) {
            polygons.forEach(polygon => {
                featureGroups.push({ layer: [polygon], metaData: {} });
            });
        }
        return featureGroups;
    }

    getAreaTypeNameSingular(areaTypeId: number): string {
        switch (areaTypeId) {
            case 5:
                return 'Poststed';
            case 6:
                return 'Grunnkrets';
            case 7:
                return 'Kommune';
            case 8:
                return 'Fylke';
            case 9:
                return 'Delbydel';
            case 10:
                return 'Kommunedel';
            case 11:
                return 'Bydel';
            default:
                return areaTypeId.toString();
        }
    }

    // Convert ILatLng to L.LatLng
    private convertToLeafletLatLng(latlng: ILatLng): L.LatLng {
        return L.latLng(latlng.lat, latlng.lng);
    }
    
    // Convert ILatLng[][][] to L.LatLng[][][]
    convertToLeafletLatLngArray3D(array3D: ILatLng[][][]): L.LatLng[][][] {
        return array3D.map(array2D => 
            array2D.map(array1D => 
                array1D.map(latlng => 
                    this.convertToLeafletLatLng(latlng)
                )
            )
        );
    }

    private getDisplayName(area: TemporaryArea): string {    
        if (area.areaType === EvAreaType.PostcodeArea) {
            const postnumber = area.number;
            const postcode = '0000' + postnumber;
            return postcode.substring(postcode.length - 4, postcode.length);
        }
        return CapitalizePipe.transform(area.name);
    }
}